import { useRef, useEffect } from "react";
import { TextInput, type TextStyle } from "react-native";
import Animated, {
  useSharedValue,
  withTiming,
  useAnimatedProps,
  Easing,
} from "react-native-reanimated";
import { haptic } from "../haptics";

const AnimatedTextInput = Animated.createAnimatedComponent(TextInput);

type Props = {
  value: number;
  style?: TextStyle;
  className?: string;
  /** Prefix shown before the number (e.g. "~$") */
  prefix?: string;
  /** Suffix shown after the number (e.g. " €") */
  suffix?: string;
};

/** Worklet-safe integer formatting with comma separators */
function formatWithCommas(n: number): string {
  "worklet";
  const rounded = Math.round(Math.abs(n));
  const str = rounded.toString();
  const parts: string[] = [];
  for (let i = str.length; i > 0; i -= 3) {
    parts.unshift(str.slice(Math.max(0, i - 3), i));
  }
  return (n < 0 ? "-" : "") + parts.join(",");
}

export function AnimatedNumber({
  value,
  style,
  className,
  prefix = "",
  suffix = "",
}: Props) {
  const animatedValue = useSharedValue(value);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      animatedValue.value = value;
      return;
    }

    animatedValue.value = withTiming(value, {
      duration: 400,
      easing: Easing.out(Easing.cubic),
    });

    haptic("light");
  }, [value, animatedValue]);

  const animatedProps = useAnimatedProps(() => {
    const formatted = formatWithCommas(animatedValue.value);
    const value = `${prefix}${formatted}${suffix}`;
    return {
      text: value,
      defaultValue: value,
    };
  });

  return (
    <AnimatedTextInput
      className={className}
      editable={false}
      underlineColorAndroid="transparent"
      style={[{ padding: 0 }, style]}
      animatedProps={animatedProps}
    />
  );
}
